import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import './TrainingCountdown.css'

const TICK_MS = 250
const TIMER_STORE_PREFIX = 'ybg-train-timer-v2'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatClock(ms, { overtime = false } = {}) {
  const total = Math.max(0, Math.floor(Math.abs(ms) / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const body = hours > 0
    ? `${hours}:${pad2(minutes)}:${pad2(seconds)}`
    : `${pad2(minutes)}:${pad2(seconds)}`
  return overtime ? `+${body}` : body
}

function readTimerRecord(key) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null')
    const startedAt = Number(parsed?.startedAt)
    if (!(startedAt > 0)) return null
    return {
      startedAt,
      timeUpAcked: Boolean(parsed?.timeUpAcked),
    }
  } catch (_) {
    return null
  }
}

function writeTimerRecord(key, record) {
  try {
    window.localStorage.setItem(key, JSON.stringify(record))
  } catch (_) { /* 隐私模式等写失败时仍用内存计时 */ }
}

export function trainingTimerStorageKey(userKey, taskId, kind) {
  return `${TIMER_STORE_PREFIX}:${userKey || 'anon'}:${kind || 'ccma'}:${taskId}`
}

/** 点「开始训练」时写入新的起点，避免沿用上次测试留下的超时时钟。 */
export function startTrainingTimerClock({ userKey, taskId, kind }) {
  const startedAt = Date.now()
  writeTimerRecord(trainingTimerStorageKey(userKey, taskId, kind), {
    startedAt,
    timeUpAcked: false,
  })
  return startedAt
}

/**
 * 训练完成时限倒计时牌。
 * minutes <= 0 或缺少 taskId 时不渲染任何 DOM。
 * placement=viewport：贴视口右上（专项/群聊）。
 * placement=panel：贴文件面板右上，不遮挡综合训练进度条。
 */
export default function TrainingCountdown({
  taskId,
  minutes,
  userKey,
  compact = false,
  placement = 'viewport',
  kind = 'ccma',
  startedAt: startedAtProp = null,
}) {
  const enabled = Number(minutes) > 0 && Number(taskId) > 0
  const storageKey = enabled
    ? trainingTimerStorageKey(userKey, taskId, kind)
    : ''

  const [now, setNow] = useState(() => Date.now())
  const [startedAt, setStartedAt] = useState(null)
  const [acked, setAcked] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setStartedAt(null)
      setAcked(false)
      return undefined
    }
    const launchedAt = Number(startedAtProp)
    const existing = launchedAt > 0 ? null : readTimerRecord(storageKey)
    const nextStartedAt = launchedAt > 0 ? launchedAt : (existing?.startedAt || Date.now())
    writeTimerRecord(storageKey, {
      startedAt: nextStartedAt,
      timeUpAcked: launchedAt > 0 ? false : Boolean(existing?.timeUpAcked),
    })
    setStartedAt(nextStartedAt)
    setAcked(launchedAt > 0 ? false : Boolean(existing?.timeUpAcked))
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(id)
  }, [enabled, storageKey, startedAtProp])

  const remainingMs = useMemo(() => {
    if (!enabled || !startedAt) return 0
    return startedAt + Number(minutes) * 60 * 1000 - now
  }, [enabled, startedAt, minutes, now])

  if (!enabled || !startedAt) return null

  const overtime = remainingMs <= 0
  const remainingAbs = overtime ? 0 : remainingMs
  const tone = overtime
    ? 'over'
    : remainingAbs <= 60 * 1000
      ? 'danger'
      : remainingAbs <= 5 * 60 * 1000
        ? 'warn'
        : 'ok'
  const showOverlay = overtime && !acked

  const ackTimeUp = () => {
    setAcked(true)
    writeTimerRecord(storageKey, { startedAt, timeUpAcked: true })
  }

  return (
    <>
      <div
        className={`ybg-train-timer ybg-train-timer--${tone}${compact ? ' ybg-train-timer--compact' : ''}${placement === 'panel' ? ' ybg-train-timer--in-panel' : ''}${placement === 'header' ? ' ybg-train-timer--in-header' : ''}`}
        role="timer"
        aria-live="polite"
      >
        <div className="ybg-train-timer__chip">
          <div className="ybg-train-timer__meta">
            <span className="ybg-train-timer__label">{overtime ? '时间到' : '剩余'}</span>
            {overtime ? <span className="ybg-train-timer__hint">超时</span> : null}
          </div>
          <span className="ybg-train-timer__digits">
            {formatClock(overtime ? -remainingMs : remainingMs, { overtime })}
          </span>
        </div>
      </div>
      {showOverlay ? createPortal(
        <div className="ybg-train-timer__overlay" role="alertdialog" aria-modal="true" aria-labelledby="ybg-train-timer-title">
          <div className="ybg-train-timer__card">
            <div className="ybg-train-timer__card-kicker">完成时限</div>
            <h2 id="ybg-train-timer-title" className="ybg-train-timer__card-title">时间到</h2>
            <p className="ybg-train-timer__card-body">
              建议时长已到。训练不会关闭，输入也不会锁定，可以继续完成后再提交。
            </p>
            <button type="button" className="ybg-train-timer__card-btn" onClick={ackTimeUp}>
              继续训练
            </button>
          </div>
        </div>,
        document.body
      ) : null}
    </>
  )
}
